import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Clapperboard, Film, Package, Loader2, Plus, Trash2, Copy, Check,
  ChevronDown, ChevronRight, AlertCircle, CheckCircle, Video, ExternalLink, X,
  Folder, FolderOpen, RefreshCw,
} from 'lucide-react';
import { labApi } from '@/lib/api';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { VoicePicker, type ProductionVoiceOption } from './ProductionPickers';
import { SceneTableEditor, type VideoSceneRecord } from './SceneTableEditor';
import { AvatarWorkshopPanel } from './AvatarWorkshopPanel';

interface VideoAssetKindDef {
  kind: string;
  label: string;
  description: string;
  pipeline: string;
  requiresKind: string | null;
}

interface VideoProductionKindDef {
  kind: string;
  label: string;
  description: string;
  requiresKind: string;
  providerByProfile: Record<string, string>;
}

interface VideoStrategyDef {
  key: string;
  productionProfile: string;
}

// Modele wideo Kie.ai do produkcji b-rolli (rejestr LAB_VIDEO_MODELS)
interface VideoModelOption {
  key: string;
  label: string;
  description: string;
  costHint: string;
  clipSeconds: number;
  supportsImageInput: boolean;
  recommended: boolean;
}

interface VideoAssetRecord {
  id: string;
  name: string;
  content: string | null;
  status: string;
  createdAt: string;
  metadata?: {
    kind?: string;
    generation?: string;
    error?: string;
    productionProfile?: string;
    production?: boolean;
    driveFileUrl?: string;
    provider?: string;
    voiceId?: string;
    avatarId?: string;
    scenes?: VideoSceneRecord[];
    kieShots?: Array<{
      shotId: string;
      prompt: string;
      state: 'submitted' | 'done' | 'failed';
      driveFileUrl?: string;
      error?: string;
    }>;
  } | null;
}

interface VideoAssetsPanelProps {
  productId: string;
  contentId: string;
  contentType: string;
  strategy: string | null;
}

const KIND_ICONS: Record<string, typeof Clapperboard> = {
  SCENES: Clapperboard,
  BROLL: Film,
  EDITOR_BRIEF: Package,
  VIDEO_MAIN: Video,
  BROLL_VIDEO: Film,
};

const POLL_INTERVAL_MS = 6000;

export function VideoAssetsPanel({ productId, contentId, contentType, strategy }: VideoAssetsPanelProps) {
  const [kinds, setKinds] = useState<VideoAssetKindDef[]>([]);
  const [productionKinds, setProductionKinds] = useState<VideoProductionKindDef[]>([]);
  const [strategies, setStrategies] = useState<VideoStrategyDef[]>([]);
  const [videoModels, setVideoModels] = useState<VideoModelOption[]>([]);
  const [productionModal, setProductionModal] = useState<VideoProductionKindDef | null>(null);
  const [generateModal, setGenerateModal] = useState<VideoAssetKindDef | null>(null);
  const [assets, setAssets] = useState<VideoAssetRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});
  const [expandedAssetId, setExpandedAssetId] = useState<string | null>(null);
  const [copiedAssetId, setCopiedAssetId] = useState<string | null>(null);
  const [deleteAsset, setDeleteAsset] = useState<VideoAssetRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadAssets = useCallback(async () => {
    try {
      const res = await labApi.getVideoAssets(productId, contentId);
      setAssets(res.data.data || []);
    } catch (err) {
      console.error('Failed to load video assets:', err);
    }
  }, [productId, contentId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [kindsRes] = await Promise.all([
          labApi.getVideoStrategies(),
          loadAssets(),
        ]);
        if (!cancelled) {
          setKinds(kindsRes.data.data?.assetKinds || []);
          setProductionKinds(kindsRes.data.data?.productionKinds || []);
          setStrategies(kindsRes.data.data?.strategies || []);
          setVideoModels(kindsRes.data.data?.videoModels || []);
        }
      } catch (err) {
        console.error('Failed to load video asset kinds:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadAssets]);

  // Polling dopóki którykolwiek zasób jest w trakcie generowania
  const hasPending = assets.some(asset => ['pending', 'queued', 'processing'].includes(asset.metadata?.generation || ''));
  useEffect(() => {
    if (!hasPending) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(() => void loadAssets(), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [hasPending, loadAssets]);

  const readyKinds = new Set(
    assets.filter(asset => asset.status === 'READY').map(asset => asset.metadata?.kind).filter(Boolean),
  );
  const assetsByKind = new Map<string, VideoAssetRecord[]>();
  for (const asset of assets) {
    const kind = asset.metadata?.kind || 'INNE';
    const list = assetsByKind.get(kind) || [];
    list.push(asset);
    assetsByKind.set(kind, list);
  }

  // Legacy treści bez strategii: UGC_SCRIPT = HeyGen, reszta = ElevenLabs
  const strategyKey = (strategy || '').trim().toUpperCase();
  const profile = strategies.find(item => item.key === strategyKey)?.productionProfile
    || (contentType === 'UGC_SCRIPT' ? 'heygen' : 'elevenlabs');

  const handleGenerate = async (kind: VideoAssetKindDef, instruction?: string) => {
    setError(null);
    try {
      await labApi.generateVideoAsset(productId, contentId, { kind: kind.kind, instruction: instruction || undefined });
      setExpandedFolders(prev => ({ ...prev, [kind.kind]: true }));
      await loadAssets();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nie udało się rozpocząć generowania zasobu.');
      throw err;
    }
  };

  const handleCopy = (asset: VideoAssetRecord) => {
    if (!asset.content) return;
    navigator.clipboard.writeText(asset.content);
    setCopiedAssetId(asset.id);
    setTimeout(() => setCopiedAssetId(null), 2000);
  };

  const handleDelete = async () => {
    if (!deleteAsset) return;
    try {
      await labApi.deleteVideoAsset(productId, deleteAsset.id);
      setDeleteAsset(null);
      await loadAssets();
    } catch (err) {
      console.error('Failed to delete video asset:', err);
      setDeleteAsset(null);
    }
  };

  const assetStatusBadge = (asset: VideoAssetRecord) => {
    const generation = asset.metadata?.generation;
    if (generation === 'pending' || generation === 'queued' || generation === 'processing') {
      const label = generation === 'queued' ? 'W kolejce produkcji...' : generation === 'processing' ? 'Produkcja w toku...' : 'Generowanie...';
      return (
        <span className="flex items-center gap-1 text-[10px] font-medium text-warning">
          <Loader2 className="w-3 h-3 animate-spin" /> {label}
        </span>
      );
    }
    if (generation === 'error') {
      return (
        <span className="flex items-center gap-1 text-[10px] font-medium text-red-400">
          <AlertCircle className="w-3 h-3" /> Błąd
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-[10px] font-medium text-success">
        <CheckCircle className="w-3 h-3" /> Gotowy
      </span>
    );
  };

  const renderAssetRow = (asset: VideoAssetRecord) => {
    const Icon = KIND_ICONS[asset.metadata?.kind || ''] || Package;
    const isAssetExpanded = expandedAssetId === asset.id;
    return (
      <div key={asset.id} className="bg-surface-raised/70 rounded-lg border border-border-subtle overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2">
          <button
            onClick={() => setExpandedAssetId(isAssetExpanded ? null : asset.id)}
            className="flex items-center gap-2 flex-1 min-w-0 text-left"
          >
            {isAssetExpanded
              ? <ChevronDown className="w-3.5 h-3.5 text-text-muted shrink-0" />
              : <ChevronRight className="w-3.5 h-3.5 text-text-muted shrink-0" />}
            <Icon className="w-4 h-4 text-accent-info shrink-0" />
            <span className="text-xs font-medium text-text-primary truncate">{asset.name}</span>
            {asset.metadata?.kind === 'SCENES' && Array.isArray(asset.metadata?.scenes) && asset.metadata.scenes.length > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-accent-primary/15 text-accent-primary border border-accent-primary/20 shrink-0">
                Edytor ujęć
              </span>
            )}
            {assetStatusBadge(asset)}
            <span className="text-[10px] text-text-disabled ml-auto shrink-0">
              {new Date(asset.createdAt).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
          </button>
          <button
            onClick={() => handleCopy(asset)}
            disabled={!asset.content}
            className="p-1 text-text-muted hover:text-accent-info transition-colors disabled:opacity-30"
            title="Kopiuj treść"
            aria-label="Kopiuj treść zasobu"
          >
            {copiedAssetId === asset.id ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => setDeleteAsset(asset)}
            className="p-1 text-text-muted hover:text-red-400 transition-colors"
            title="Usuń zasób"
            aria-label="Usuń zasób"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        {isAssetExpanded && (
          <div className="px-3 pb-3">
            {asset.metadata?.generation === 'error' ? (
              <p className="text-[11px] text-red-400">{asset.metadata?.error || 'Generowanie nie powiodło się.'}</p>
            ) : Array.isArray(asset.metadata?.scenes) && asset.metadata.scenes.length > 0 ? (
              <SceneTableEditor
                key={asset.id}
                productId={productId}
                assetId={asset.id}
                scenes={asset.metadata.scenes}
                showGestures={profile === 'heygen'}
                onSaved={() => void loadAssets()}
              />
            ) : (asset.metadata?.kieShots?.length || asset.metadata?.driveFileUrl) ? (
              <div className="space-y-2">
                {asset.content && !asset.metadata?.kieShots?.length && (
                  <p className="text-[11px] text-text-secondary whitespace-pre-wrap">{asset.content}</p>
                )}
                {/* Ujęcia Kie.ai: status + link + regeneracja pojedynczego klipu */}
                {(asset.metadata?.kieShots || []).map(shot => (
                  <div key={shot.shotId} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-border-subtle bg-surface-hover/30">
                    <span className="min-w-0 flex-1">
                      <span className="text-[11px] font-medium text-text-primary block truncate">Ujęcie {shot.shotId}</span>
                      {shot.state === 'failed' && <span className="text-[10px] text-red-400 block truncate" title={shot.error}>{shot.error || 'Błąd generacji'}</span>}
                      {shot.state === 'submitted' && <span className="text-[10px] text-warning block">Generowanie...</span>}
                    </span>
                    {shot.state === 'done' && shot.driveFileUrl && (
                      <a href={shot.driveFileUrl} target="_blank" rel="noreferrer" className="p-1 text-text-muted hover:text-accent-primary transition-colors shrink-0" title="Otwórz klip" aria-label="Otwórz klip na Google Drive">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {shot.state === 'submitted' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-warning shrink-0" />
                    ) : (
                      <button
                        onClick={() => labApi.retryKieShot(productId, asset.id, shot.shotId).then(() => void loadAssets())}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-text-secondary bg-surface-hover/40 border border-border rounded-lg hover:border-border-strong hover:text-text-primary transition-all shrink-0"
                        title="Wygeneruj to ujęcie ponownie"
                      >
                        <RefreshCw className="w-3 h-3" /> Regeneruj
                      </button>
                    )}
                  </div>
                ))}
                {asset.metadata?.driveFileUrl && (
                  <a
                    href={asset.metadata.driveFileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent-primary bg-accent-primary/10 border border-accent-primary/20 rounded-lg hover:bg-accent-primary/15 transition-all"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> {asset.metadata?.kieShots?.length ? 'Otwórz folder na Google Drive' : 'Otwórz plik na Google Drive'}
                  </a>
                )}
              </div>
            ) : asset.content ? (
              <div className="space-y-2">
                {asset.metadata?.kind === 'SCENES' && (
                  <p className="text-[11px] text-warning">
                    Ten skrypt produkcyjny powstał przed edytorem ujęć. Wygeneruj nowy wariant, żeby edytować sceny i przypisywać avatary per ujęcie.
                  </p>
                )}
                <pre className="text-[11px] text-text-secondary bg-surface-base/60 rounded-lg border border-border p-3 whitespace-pre-wrap max-h-96 overflow-y-auto font-mono">
                  {asset.content}
                </pre>
              </div>
            ) : (
              <p className="text-[11px] text-text-muted">Treść pojawi się po zakończeniu generowania.</p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Wspólny folder dla zasobów tekstowych i produkcyjnych — nagłówek + zawartość
  const renderFolder = (args: {
    kind: string;
    label: string;
    description: string;
    chainBlocked: boolean;
    requiredLabel: string | null;
    action: { label: string; onClick: () => void; disabled?: boolean; disabledHint?: string } | null;
  }) => {
    const folderAssets = assetsByKind.get(args.kind) || [];
    const isOpen = Boolean(expandedFolders[args.kind]);
    const FolderIcon = isOpen ? FolderOpen : Folder;
    const KindIcon = KIND_ICONS[args.kind] || Package;
    const pendingInFolder = folderAssets.some(asset => ['pending', 'queued', 'processing'].includes(asset.metadata?.generation || ''));

    return (
      <div key={args.kind} className="bg-surface-hover/30 rounded-lg border border-border overflow-hidden">
        <button
          onClick={() => setExpandedFolders(prev => ({ ...prev, [args.kind]: !prev[args.kind] }))}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-surface-hover/40 transition-colors"
        >
          <FolderIcon className="w-4 h-4 text-accent-info shrink-0" />
          <KindIcon className="w-3.5 h-3.5 text-text-muted shrink-0" />
          <span className="text-xs font-semibold text-text-primary">{args.label}</span>
          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-surface-raised border border-border-subtle text-text-secondary">
            {folderAssets.length}
          </span>
          {pendingInFolder && <Loader2 className="w-3 h-3 animate-spin text-warning shrink-0" />}
          {args.chainBlocked && (
            <span className="text-[10px] text-text-disabled truncate">najpierw: {args.requiredLabel}</span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-text-muted ml-auto shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="px-3 pb-3 space-y-2 border-t border-border-subtle pt-3">
            <p className="text-[10px] text-text-muted">{args.description}</p>
            {args.action && (
              <button
                onClick={args.action.onClick}
                disabled={args.action.disabled}
                title={args.action.disabled ? args.action.disabledHint : undefined}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent-info bg-info/10 border border-accent-info/20 rounded-lg hover:bg-info/15 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="w-3.5 h-3.5" />
                {args.action.label}
              </button>
            )}
            {folderAssets.length === 0 ? (
              <p className="text-[11px] text-text-disabled">Folder pusty - brak wygenerowanych zasobów.</p>
            ) : (
              <div className="space-y-2">{folderAssets.map(renderAssetRow)}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-surface-raised rounded-xl border border-accent-info/20 overflow-hidden">
      <div className="px-4 py-3 bg-info/5 border-b border-accent-info/15 flex items-center gap-2">
        <Clapperboard className="w-4 h-4 text-accent-info" />
        <span className="text-xs font-semibold text-text-primary">Zasoby produkcyjne</span>
        <span className="text-[10px] text-text-muted">skrypt → sceny → b-rolle → produkcja</span>
      </div>

      <div className="p-4 space-y-2">
        {error && (
          <div className="flex items-center gap-2 text-[11px] text-red-400">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 py-4 text-xs text-text-muted">
            <Loader2 className="w-4 h-4 animate-spin" /> Ładowanie zasobów...
          </div>
        ) : (
          <>
            {/* KROK 0 (profil HeyGen): kreator avatarów - postaci i sceny do przypisania w edytorze ujęć */}
            {profile === 'heygen' && <AvatarWorkshopPanel productId={productId} />}

            {/* Foldery zasobów tekstowych (kolejność = łańcuch) */}
            {kinds.map(kind => {
              const chainBlocked = Boolean(kind.requiresKind && !readyKinds.has(kind.requiresKind));
              const requiredLabel = kind.requiresKind
                ? kinds.find(item => item.kind === kind.requiresKind)?.label || kind.requiresKind
                : null;
              return renderFolder({
                kind: kind.kind,
                label: kind.label,
                description: kind.description,
                chainBlocked,
                requiredLabel,
                action: {
                  label: (assetsByKind.get(kind.kind) || []).length > 0 ? 'Generuj nowy wariant' : 'Generuj',
                  onClick: () => setGenerateModal(kind),
                  disabled: chainBlocked,
                  disabledHint: `Najpierw wygeneruj: ${requiredLabel}`,
                },
              });
            })}

            {/* Foldery produkcji (realne pliki przez API). Kadry ujęć żyją
                w Banku wideo (per produkt) - stary kreator "Zdjęcia b-roll"
                (relikt Higgsfielda) wycięty z flow. */}
            {profile !== 'none' && productionKinds.map(kind => {
              const provider = kind.providerByProfile[profile];
              if (!provider) return null;
              const chainBlocked = !readyKinds.has(kind.requiresKind);
              const requiredLabel = kinds.find(item => item.kind === kind.requiresKind)?.label || kind.requiresKind;
              return renderFolder({
                kind: kind.kind,
                label: kind.label,
                description: kind.description,
                chainBlocked,
                requiredLabel,
                action: {
                  label: 'Uruchom produkcję',
                  onClick: () => setProductionModal(kind),
                  disabled: chainBlocked,
                  disabledHint: `Najpierw wygeneruj: ${requiredLabel}`,
                },
              });
            })}
          </>
        )}
      </div>

      {generateModal && (
        <GenerateAssetVariantModal
          kind={generateModal}
          existingCount={(assetsByKind.get(generateModal.kind) || []).length}
          onGenerate={async instruction => {
            await handleGenerate(generateModal, instruction);
            setGenerateModal(null);
          }}
          onClose={() => setGenerateModal(null)}
        />
      )}

      {productionModal && (
        <VideoProductionConfigModal
          productId={productId}
          contentId={contentId}
          kind={productionModal}
          provider={productionModal.providerByProfile[profile] || 'elevenlabs'}
          models={videoModels}
          parentScenes={(assets.find(asset => asset.status === 'READY' && asset.metadata?.kind === 'SCENES' && Array.isArray(asset.metadata?.scenes) && asset.metadata.scenes.length > 0)?.metadata?.scenes) || []}
          onStarted={() => {
            setExpandedFolders(prev => ({ ...prev, [productionModal.kind]: true }));
            setProductionModal(null);
            void loadAssets();
          }}
          onClose={() => setProductionModal(null)}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(deleteAsset)}
        title="Usunąć zasób?"
        message={deleteAsset ? <>Czy na pewno chcesz usunąć <strong>{deleteAsset.name}</strong>?</> : ''}
        confirmLabel="Usuń zasób"
        danger
        onConfirm={handleDelete}
        onClose={() => setDeleteAsset(null)}
      />
    </div>
  );
}

// ============================================
// Modal generowania zasobu tekstowego (wytyczne wariantu)
// ============================================

interface GenerateAssetVariantModalProps {
  kind: VideoAssetKindDef;
  existingCount: number;
  onGenerate: (instruction: string) => Promise<void>;
  onClose: () => void;
}

function GenerateAssetVariantModal({ kind, existingCount, onGenerate, onClose }: GenerateAssetVariantModalProps) {
  const [instruction, setInstruction] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleStart = async () => {
    setStarting(true);
    try {
      await onGenerate(instruction.trim());
    } catch {
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary">
            {existingCount > 0 ? `${kind.label} - nowy wariant` : kind.label}
          </h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors" aria-label="Zamknij">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-text-muted mb-4">{kind.description}</p>

        {existingCount > 0 && (
          <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-info/5 border border-accent-info/15">
            <AlertCircle className="w-3.5 h-3.5 text-accent-info shrink-0 mt-0.5" />
            <p className="text-[11px] text-text-secondary">
              Istniejące wersje ({existingCount}) trafią do kontekstu AI z instrukcją, żeby nowy wariant był wyraźnie ODMIENNY. Możesz dodatkowo wskazać kierunek poniżej.
            </p>
          </div>
        )}

        <div>
          <label htmlFor="asset-variant-instruction" className="text-xs font-medium text-text-secondary mb-1.5 block">
            Kierunek wariantu (opcjonalnie)
          </label>
          <textarea
            id="asset-variant-instruction"
            value={instruction}
            onChange={e => setInstruction(e.target.value)}
            placeholder={existingCount > 0
              ? 'np. bardziej dokumentalny styl, ujęcia w kuchni zamiast łazienki, młodsza grupa docelowa...'
              : 'np. spokojniejsze tempo, nacisk na mechanizm działania...'}
            rows={3}
            className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
          />
        </div>

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={handleStart}
            disabled={starting}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
          >
            {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Generuj
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"
          >
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Modal konfiguracji produkcji (głos / avatar + wytyczne)
// ============================================

interface VideoProductionConfigModalProps {
  productId: string;
  contentId: string;
  kind: VideoProductionKindDef;
  provider: string;
  models: VideoModelOption[];
  parentScenes: VideoSceneRecord[];
  onStarted: () => void;
  onClose: () => void;
}

// Modal produkcji: TYLKO głos + jakość + zakres. Avatary per scena są już
// przypisane w Skrypcie produkcyjnym (edytor ujęć / Kreator avatarów).
// B-rolle: wybór modelu wideo Kie.ai (karty) zamiast głosu - klipy są nieme.
function VideoProductionConfigModal({ productId, contentId, kind, provider, models, parentScenes, onStarted, onClose }: VideoProductionConfigModalProps) {
  const [voices, setVoices] = useState<ProductionVoiceOption[]>([]);
  const [voiceId, setVoiceId] = useState('');
  const [instruction, setInstruction] = useState('');
  const [quality, setQuality] = useState<'test' | 'final'>('final');
  const [modelKey, setModelKey] = useState(() => {
    const recommended = models.find(model => model.recommended);
    return recommended?.key || models[0]?.key || '';
  });
  // Źródło b-rolli: bank / hybryda / od zera (bank wymaga gotowych klipów)
  const [brollSource, setBrollSource] = useState<'bank' | 'hybrid' | 'generate'>('generate');
  const [bankReadyCount, setBankReadyCount] = useState<number | null>(null);
  // Audio-first: głos ElevenLabs napędza sceny HeyGen (rekomendowane -
  // identyczny głos w całej kreacji); heygen = legacy TTS
  const [voiceSource, setVoiceSource] = useState<'elevenlabs' | 'heygen'>('elevenlabs');
  const [pairedVoiceId, setPairedVoiceId] = useState<string | null>(null);
  // Hook lip-sync: scena mówiona + kadr źródłowy z Banku wideo
  const [hookSceneId, setHookSceneId] = useState<number | null>(null);
  const [hookImageClipId, setHookImageClipId] = useState<string | null>(null);
  const [bankImageClips, setBankImageClips] = useState<Array<{ id: string; name: string; updatedAt: string }>>([]);
  const [scope, setScope] = useState<'full' | 'scenes'>('full');
  const [selectedSceneIds, setSelectedSceneIds] = useState<Set<number>>(new Set());
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [optionsWarnings, setOptionsWarnings] = useState<string[]>([]);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [starting, setStarting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const isBroll = kind.kind === 'BROLL_VIDEO';
  const isHook = kind.kind === 'HOOK_VIDEO';
  const isHiggsfield = (provider === 'higgsfield' || isBroll) && !isHook;
  const isHeyGen = provider === 'heygen' && !isBroll && !isHook;

  // B-rolle: liczba gotowych klipów w Banku decyduje o trybach.
  // Hook: kadry banku (z wygenerowanym obrazem) jako źródło lip-synca.
  useEffect(() => {
    if (!isBroll && !isHook) return;
    let cancelled = false;
    labApi.getVideoBank(productId)
      .then(res => {
        if (cancelled) return;
        const clips = res.data.data?.clips || [];
        const ready = clips.filter((clip: any) => clip.status === 'READY').length;
        setBankReadyCount(ready);
        if (isBroll && ready > 0) setBrollSource('hybrid');
        if (isHook) {
          setBankImageClips(clips
            .filter((clip: any) => ['IMAGE_READY', 'VIDEO_QUEUED', 'VIDEO_PROCESSING', 'READY'].includes(clip.status))
            .map((clip: any) => ({ id: clip.id, name: clip.name, updatedAt: clip.updatedAt })));
        }
      })
      .catch(() => { if (!cancelled) setBankReadyCount(0); });
    return () => { cancelled = true; };
  }, [isBroll, isHook, productId]);

  // Para postać+głos: preselekcja głosu ElevenLabs przypisanego postaci
  // z pierwszej sceny skryptu produkcyjnego (audio-first)
  useEffect(() => {
    if (!isHeyGen) return;
    const lookIds = parentScenes.map(scene => scene.look_id).filter(Boolean) as string[];
    if (lookIds.length === 0) return;
    let cancelled = false;
    labApi.getAvatars()
      .then(res => {
        if (cancelled) return;
        const avatars = res.data.data || [];
        for (const lookId of lookIds) {
          const match = avatars.find((avatar: any) => avatar.heygenAvatarId === lookId
            || (avatar.scenes || []).some((scene: any) => scene.heygenLookId === lookId));
          if (match?.defaultVoiceId) {
            setPairedVoiceId(match.defaultVoiceId);
            return;
          }
        }
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [isHeyGen, parentScenes]);

  useEffect(() => {
    // Higgsfield nie ma głosów - konfigurator ogranicza się do wytycznych
    if (isHiggsfield) {
      setLoadingOptions(false);
      return;
    }
    // Audio-first: sceny HeyGen sterowane audio ElevenLabs - lista głosów
    // pochodzi wtedy z ElevenLabs, nie z HeyGena. Hook zawsze ElevenLabs.
    const voiceProvider = isHook || (isHeyGen && voiceSource === 'elevenlabs') ? 'elevenlabs' : provider;
    let cancelled = false;
    setLoadingOptions(true);
    setModalError(null);
    (async () => {
      try {
        const res = await labApi.getVideoProductionOptions(voiceProvider);
        if (cancelled) return;
        const data = res.data.data || {};
        const list = data.voices || [];
        setVoices(list);
        setOptionsWarnings(data.warnings || []);
        // Preselekcja: głos sparowany z postacią > pierwszy z listy
        const paired = pairedVoiceId && list.some((voice: any) => voice.voiceId === pairedVoiceId) ? pairedVoiceId : null;
        if (paired) setVoiceId(paired);
        else if (list.length > 0) setVoiceId(list[0].voiceId);
      } catch (err: any) {
        if (!cancelled) setModalError(err?.response?.data?.error || 'Nie udało się pobrać opcji produkcji. Sprawdź integrację w Integracje → Produkcja reklam AI.');
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => { cancelled = true; };
  }, [provider, isHiggsfield, isHeyGen, isHook, voiceSource, pairedVoiceId, reloadNonce]);

  const toggleScene = (sceneId: number) => {
    setSelectedSceneIds(prev => {
      const next = new Set(prev);
      if (next.has(sceneId)) next.delete(sceneId);
      else next.add(sceneId);
      return next;
    });
  };

  const missingLooks = isHeyGen && parentScenes.length > 0 && !parentScenes.some(scene => scene.look_id);

  const handleStart = async () => {
    setModalError(null);
    setStarting(true);
    try {
      await labApi.startVideoProduction(productId, contentId, {
        kind: kind.kind,
        voiceId: voiceId || undefined,
        instruction: instruction.trim() || undefined,
        ...(isHeyGen && { quality, voiceSource }),
        ...(isBroll && modelKey && brollSource !== 'bank' && { modelKey }),
        ...(isBroll && { brollSource }),
        ...(isHook && {
          ...(hookSceneId !== null && { sceneIds: [hookSceneId] }),
          ...(hookImageClipId && { hookImageBankClipId: hookImageClipId }),
        }),
        ...(!isHiggsfield && !isHook && {
          scope,
          ...(scope === 'scenes' && { sceneIds: Array.from(selectedSceneIds) }),
        }),
      });
      onStarted();
    } catch (err: any) {
      setModalError(err?.response?.data?.error || 'Nie udało się uruchomić produkcji.');
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-raised rounded-xl border border-border w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary">{kind.label}</h3>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors" aria-label="Zamknij">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-text-muted mb-4">
          {isHeyGen
            ? 'Klipy UGC per scena z avatarami przypisanymi w Skrypcie produkcyjnym. Gotowe pliki trafią na Google Drive.'
            : isHook
              ? 'Mówione ujęcie kreatywne: audio ElevenLabs (język gwarantowany) + wybrany kadr, animowane lip-sync. Gotowy klip trafi na Google Drive.'
              : isBroll
                ? 'Nieme klipy b-roll na bazie zdjęć b-roll lub promptów ujęć (max 8). Dźwięk reklamy pochodzi z warstwy narratora/lektora. Gotowe pliki trafią na Google Drive.'
                : 'Audio lektora ElevenLabs na bazie skryptu produkcyjnego. Gotowy plik trafi na Google Drive.'}
        </p>

        {missingLooks && (
          <div className="flex items-start gap-2 p-2.5 mb-3 rounded-lg bg-warning/10 border border-warning/20 text-[11px] text-warning">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            Żadna scena nie ma przypisanego avatara - otwórz Skrypt produkcyjny i wybierz ujęcia (kolumna "Ujęcie") przed startem.
          </div>
        )}

        {loadingOptions ? (
          <div className="flex items-center gap-2 py-6 text-xs text-text-muted">
            <Loader2 className="w-4 h-4 animate-spin" /> Pobieranie głosów... Pierwsze pobranie może potrwać do 2 minut, kolejne są natychmiastowe.
          </div>
        ) : (
          <div className="space-y-3">
            {optionsWarnings.map((warning, index) => (
              <div key={index} className="flex items-start gap-2 p-2.5 rounded-lg bg-warning/10 border border-warning/20 text-[11px] text-warning">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span className="flex-1">{warning}</span>
                <button
                  onClick={() => setReloadNonce(n => n + 1)}
                  className="text-[11px] font-medium underline hover:no-underline shrink-0"
                >
                  Spróbuj ponownie
                </button>
              </div>
            ))}

            {isHook && (
              <div>
                <span className="text-xs font-medium text-text-secondary mb-1.5 block">Scena hooka</span>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border-subtle bg-surface-hover/30">
                  {parentScenes.filter(scene => scene.spoken_text).map((scene, index) => {
                    const selected = hookSceneId === scene.scene_id || (hookSceneId === null && index === 0);
                    return (
                      <label key={scene.scene_id} className="flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-surface-hover/50 transition-colors">
                        <input
                          type="radio"
                          name="hook-scene"
                          checked={selected}
                          onChange={() => setHookSceneId(scene.scene_id)}
                          className="mt-0.5 accent-current"
                        />
                        <span className="min-w-0">
                          <span className="text-[11px] font-medium text-text-primary block">Scena {scene.scene_id}{scene.section ? ` · ${scene.section}` : ''}</span>
                          <span className="text-[10px] text-text-muted block truncate">{scene.spoken_text}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {isHook && (
              <div>
                <span className="text-xs font-medium text-text-secondary mb-1.5 block">Kadr źródłowy</span>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  <button
                    type="button"
                    onClick={() => setHookImageClipId(null)}
                    className={`aspect-[3/4] rounded-lg border flex flex-col items-center justify-center gap-1 transition-all ${hookImageClipId === null ? 'border-accent-primary/50 bg-accent-primary/10' : 'border-border bg-surface-hover/30 hover:border-border-strong'}`}
                  >
                    <Video className={`w-4 h-4 ${hookImageClipId === null ? 'text-accent-primary' : 'text-text-muted'}`} />
                    <span className={`text-[10px] font-medium px-1 text-center ${hookImageClipId === null ? 'text-accent-primary' : 'text-text-secondary'}`}>Portret postaci</span>
                  </button>
                  {bankImageClips.map(clip => (
                    <button
                      key={clip.id}
                      type="button"
                      onClick={() => setHookImageClipId(clip.id)}
                      className={`aspect-[3/4] rounded-lg border overflow-hidden relative transition-all ${hookImageClipId === clip.id ? 'border-accent-primary/60 ring-1 ring-accent-primary/40' : 'border-border hover:border-border-strong'}`}
                      title={clip.name}
                    >
                      <img src={labApi.getVideoBankImageUrl(productId, clip.id, clip.updatedAt)} alt={clip.name} loading="lazy" className="w-full h-full object-cover" />
                      <span className="absolute bottom-0 inset-x-0 bg-black/60 text-[9px] text-white px-1 py-0.5 truncate">{clip.name}</span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-text-muted mt-1.5">Kadry pochodzą z Banku wideo - postać w kreatywnym ujęciu przemówi Twoim audio.</p>
              </div>
            )}

            {isBroll && (
              <div>
                <span className="text-xs font-medium text-text-secondary mb-1.5 block">Źródło b-rolli</span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {([
                    { key: 'hybrid' as const, label: 'Bank + dogeneruj', hint: `Klipy z Banku (${bankReadyCount ?? '...'}) + AI dogeneruje max 3 brakujące istotne ujęcia`, disabled: !bankReadyCount },
                    { key: 'bank' as const, label: 'Tylko Bank wideo', hint: 'Zero generacji - paczka z gotowych klipów banku', disabled: !bankReadyCount },
                    { key: 'generate' as const, label: 'Generuj od zera', hint: 'Wszystkie ujęcia generowane na nowo (najdrożej)', disabled: false },
                  ]).map(option => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setBrollSource(option.key)}
                      disabled={option.disabled}
                      title={option.disabled ? 'Bank ujęć nie ma gotowych klipów (zakładka Bank wideo)' : undefined}
                      className={`p-2.5 rounded-lg border text-left transition-all disabled:opacity-40 disabled:cursor-not-allowed ${brollSource === option.key ? 'border-accent-primary/40 bg-accent-primary/5' : 'border-border bg-surface-hover/30 hover:border-border-strong'}`}
                    >
                      <span className={`text-xs font-medium block ${brollSource === option.key ? 'text-accent-primary' : 'text-text-primary'}`}>{option.label}</span>
                      <span className="text-[10px] text-text-muted block mt-0.5">{option.hint}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isBroll && brollSource !== 'bank' && models.length > 0 && (
              <div>
                <span className="text-xs font-medium text-text-secondary mb-1.5 block">Model wideo</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {models.map(model => (
                    <button
                      key={model.key}
                      type="button"
                      onClick={() => setModelKey(model.key)}
                      className={`p-2.5 rounded-lg border text-left transition-all ${modelKey === model.key ? 'border-accent-primary/40 bg-accent-primary/5' : 'border-border bg-surface-hover/30 hover:border-border-strong'}`}
                    >
                      <span className="flex items-center gap-1.5">
                        <span className={`text-xs font-medium ${modelKey === model.key ? 'text-accent-primary' : 'text-text-primary'}`}>{model.label}</span>
                        {model.recommended && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-accent-primary/15 text-accent-primary border border-accent-primary/20">
                            Rekomendowany
                          </span>
                        )}
                      </span>
                      <span className="text-[10px] text-text-muted block mt-0.5">{model.description}</span>
                      <span className="text-[10px] text-text-secondary block mt-1">{model.costHint} · klipy ~{model.clipSeconds} s</span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-text-muted mt-1.5">
                  Wymaga aktywnej integracji Kie.ai. Spójność ujęć: gotowe Zdjęcia b-roll (z postacią jako referencją) albo Veo z referencjami - postać przypisana w scenach + packshot w Zdjęciach referencyjnych.
                </p>
              </div>
            )}

            {isHeyGen && (
              <div>
                <span className="text-xs font-medium text-text-secondary mb-1.5 block">Źródło głosu</span>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setVoiceSource('elevenlabs')}
                    className={`p-2.5 rounded-lg border text-left transition-all ${voiceSource === 'elevenlabs' ? 'border-accent-primary/40 bg-accent-primary/5' : 'border-border bg-surface-hover/30 hover:border-border-strong'}`}
                  >
                    <span className={`text-xs font-medium block ${voiceSource === 'elevenlabs' ? 'text-accent-primary' : 'text-text-primary'}`}>ElevenLabs (audio-first)</span>
                    <span className="text-[10px] text-text-muted block mt-0.5">Ten sam głos w całej kreacji, pliki audio trafiają też na Drive</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setVoiceSource('heygen')}
                    className={`p-2.5 rounded-lg border text-left transition-all ${voiceSource === 'heygen' ? 'border-accent-primary/40 bg-accent-primary/5' : 'border-border bg-surface-hover/30 hover:border-border-strong'}`}
                  >
                    <span className={`text-xs font-medium block ${voiceSource === 'heygen' ? 'text-accent-primary' : 'text-text-primary'}`}>Głos HeyGen</span>
                    <span className="text-[10px] text-text-muted block mt-0.5">TTS HeyGena (dotychczasowy tryb)</span>
                  </button>
                </div>
              </div>
            )}

            {!isHiggsfield && (
              <div>
                <span className="text-xs font-medium text-text-secondary mb-1.5 block">
                  Głos
                  {isHeyGen && voiceSource === 'elevenlabs' && pairedVoiceId && voiceId === pairedVoiceId && (
                    <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-accent-primary/15 text-accent-primary border border-accent-primary/20">
                      Głos postaci
                    </span>
                  )}
                </span>
                <VoicePicker idPrefix="production" voices={voices} value={voiceId} onChange={setVoiceId} />
              </div>
            )}

            {isHeyGen && (
              <div>
                <span className="text-xs font-medium text-text-secondary mb-1.5 block">Jakość</span>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setQuality('final')}
                    className={`p-2.5 rounded-lg border text-left transition-all ${quality === 'final' ? 'border-accent-primary/40 bg-accent-primary/5' : 'border-border bg-surface-hover/30 hover:border-border-strong'}`}
                  >
                    <span className={`text-xs font-medium block ${quality === 'final' ? 'text-accent-primary' : 'text-text-primary'}`}>Premium</span>
                    <span className="text-[10px] text-text-muted block mt-0.5">Avatar IV, 1080p, ok. $4/min</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuality('test')}
                    className={`p-2.5 rounded-lg border text-left transition-all ${quality === 'test' ? 'border-accent-primary/40 bg-accent-primary/5' : 'border-border bg-surface-hover/30 hover:border-border-strong'}`}
                  >
                    <span className={`text-xs font-medium block ${quality === 'test' ? 'text-accent-primary' : 'text-text-primary'}`}>Testowa</span>
                    <span className="text-[10px] text-text-muted block mt-0.5">Avatar III, 720p, ok. $1/min</span>
                  </button>
                </div>
              </div>
            )}

            {!isHiggsfield && !isHook && (
              <div>
                <span className="text-xs font-medium text-text-secondary mb-1.5 block">Zakres</span>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setScope('full')}
                    className={`p-2.5 rounded-lg border text-left transition-all ${scope === 'full' ? 'border-accent-primary/40 bg-accent-primary/5' : 'border-border bg-surface-hover/30 hover:border-border-strong'}`}
                  >
                    <span className={`text-xs font-medium block ${scope === 'full' ? 'text-accent-primary' : 'text-text-primary'}`}>Cały skrypt</span>
                    <span className="text-[10px] text-text-muted block mt-0.5">Wszystkie sceny</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setScope('scenes')}
                    disabled={parentScenes.length === 0}
                    title={parentScenes.length === 0 ? 'Wymaga skryptu produkcyjnego ze strukturalnymi scenami' : undefined}
                    className={`p-2.5 rounded-lg border text-left transition-all disabled:opacity-40 disabled:cursor-not-allowed ${scope === 'scenes' ? 'border-accent-primary/40 bg-accent-primary/5' : 'border-border bg-surface-hover/30 hover:border-border-strong'}`}
                  >
                    <span className={`text-xs font-medium block ${scope === 'scenes' ? 'text-accent-primary' : 'text-text-primary'}`}>Konkretne sceny</span>
                    <span className="text-[10px] text-text-muted block mt-0.5">Zaznaczasz które produkować</span>
                  </button>
                </div>

                {scope === 'scenes' && (
                  <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-border divide-y divide-border-subtle bg-surface-hover/30">
                    {parentScenes.map(scene => (
                      <label key={scene.scene_id} className="flex items-start gap-2.5 px-3 py-2 cursor-pointer hover:bg-surface-hover/50 transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedSceneIds.has(scene.scene_id)}
                          onChange={() => toggleScene(scene.scene_id)}
                          className="mt-0.5 accent-current"
                        />
                        <span className="min-w-0">
                          <span className="text-[11px] font-medium text-text-primary block">
                            Scena {scene.scene_id}{scene.section ? ` · ${scene.section}` : ''}{scene.duration ? ` · ${scene.duration}` : ''}
                            {scene.look_name ? ` · ${scene.look_name}` : ''}
                          </span>
                          <span className="text-[10px] text-text-muted block truncate">{scene.spoken_text}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-text-secondary mb-1.5 block">Wytyczne (opcjonalnie)</label>
              <textarea
                value={instruction}
                onChange={e => setInstruction(e.target.value)}
                placeholder="np. spokojniejsze tempo, pomiń sekcję CTA..."
                rows={2}
                className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
              />
            </div>
          </div>
        )}

        {modalError && (
          <div className="flex items-center gap-2 mt-3 text-[11px] text-red-400">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {modalError}
          </div>
        )}

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={handleStart}
            disabled={starting || loadingOptions || (!isHiggsfield && !voiceId) || missingLooks || (scope === 'scenes' && selectedSceneIds.size === 0)}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
          >
            {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Video className="w-3.5 h-3.5" />}
            Uruchom produkcję
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"
          >
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
